import { useEffect, useState } from "react";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, AlertTriangle, ScanSearch, Package } from "lucide-react";
import { StockDialog } from "@/admin/StockDialog";

export default function Deposits() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [deposits, setDeposits] = useState(null);
  const [modal, setModal] = useState(null); // {type, dep}
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [adjustments, setAdjustments] = useState([]);
  const [stockFor, setStockFor] = useState(null);
  const [scanning, setScanning] = useState(false);

  const load = () => {
    api.get("/admin/deposits").then((r) => setDeposits(r.data));
    api.get("/admin/deposit-adjustments").then((r) => setAdjustments(r.data));
  };
  useEffect(() => {
    load();
  }, []);

  const scan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/admin/deposit-adjustments/scan", {});
      toast.success(`${data.proposed.length} overdue invoice(s) proposed for adjustment`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setScanning(false);
    }
  };
  const act = async (adj, action) => {
    try {
      const { data } = await api.post(`/admin/deposit-adjustments/${adj.id}/${action}`, {});
      if (action === "confirm") toast.success(`Deducted ${money(data.deducted)} from deposit · balance ${money(data.balance_after)}`);
      else if (action === "refill") toast.success(`Refilled ${money(data.refilled)} · balance ${money(data.balance_after)}`);
      else toast.success("Adjustment dismissed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openModal = (type, dep) => {
    setForm(
      type === "receive"
        ? { amount: dep.credit_limit, form: "bank_transfer", reference: "" }
        : type === "shortfall"
        ? { amount: "", reason: "", month: "2026-06" }
        : {}
    );
    setModal({ type, dep });
  };

  const submit = async () => {
    setSaving(true);
    try {
      if (modal.type === "receive") {
        await api.post("/admin/deposits/receive", {
          agent_id: modal.dep.agent_id, amount: Number(form.amount),
          form: form.form, reference: form.reference,
        });
        toast.success("Deposit recorded");
      } else if (modal.type === "shortfall") {
        const { data } = await api.post("/admin/deposits/shortfall", {
          agent_id: modal.dep.agent_id, month: form.month,
          amount: Number(form.amount), reason: form.reason,
        });
        toast.success(`Recovered ${money(data.from_commission)} from commission, ${money(data.from_deposit)} from deposit`);
      } else if (modal.type === "refund") {
        const { data } = await api.post("/admin/deposits/refund", { agent_id: modal.dep.agent_id });
        toast.success(`Refunded ${money(data.refunded)}`);
      }
      setModal(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openAdj = adjustments.filter((a) => a.status !== "refilled" && a.status !== "dismissed");
  const doneAdj = adjustments.filter((a) => a.status === "refilled" || a.status === "dismissed");

  return (
    <div>
      <PageHeader
        title="Security Deposits & Credit"
        subtitle="Refundable deposits held in trust. Overdue customer invoices are adjusted against the agent's deposit at the billing cycle and refilled once the customer pays."
        actions={<Button variant="outline" onClick={scan} disabled={scanning} data-testid="scan-overdue-button">{scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ScanSearch className="w-4 h-4 mr-1" /> Scan overdue invoices</>}</Button>}
      />

      <Card className="overflow-hidden mb-6" data-testid="deposit-adjustments-panel">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="font-display font-semibold">Deposit adjustments · overdue credit</h3>
            <p className="text-xs text-muted-foreground">Proposed → confirm deduction → agent intimates payment → confirm refill</p>
          </div>
          <Badge variant="secondary">{openAdj.length} open</Badge>
        </div>
        {openAdj.length === 0 ? (
          <Empty>No open adjustments. Close a month or scan to propose overdue invoices.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="text-left font-medium px-4 py-2">Invoice</th>
                  <th className="text-left font-medium px-4 py-2">Agent</th>
                  <th className="text-left font-medium px-4 py-2">Customer</th>
                  <th className="text-left font-medium px-4 py-2">Due</th>
                  <th className="text-right font-medium px-4 py-2">Amount</th>
                  <th className="text-left font-medium px-4 py-2">Invoice paid?</th>
                  <th className="text-left font-medium px-4 py-2">Status</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {openAdj.map((a) => (
                  <tr key={a.id} className="border-b border-border/50" data-testid={`adjustment-row-${a.id}`}>
                    <td className="px-4 py-2.5 font-mono text-xs">{a.invoice_no}</td>
                    <td className="px-4 py-2.5">{a.agent_name}</td>
                    <td className="px-4 py-2.5">{a.customer_name}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{a.due_date}</td>
                    <td className="px-4 py-2.5 text-right font-medium">{money(a.deducted_amount ?? a.amount)}</td>
                    <td className="px-4 py-2.5">{a.customer_paid ? <Badge>paid {a.invoice_paid_date}</Badge> : <Badge variant="outline" className="capitalize">{a.invoice_payment_status}</Badge>}</td>
                    <td className="px-4 py-2.5">
                      <Badge variant={a.status === "proposed" ? "secondary" : a.status === "refill_requested" ? "default" : "destructive"} className="capitalize">{a.status.replace("_", " ")}</Badge>
                      {a.refill_note && <p className="text-[11px] text-muted-foreground mt-1 max-w-[180px]">"{a.refill_note}"</p>}
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      {a.status === "proposed" && (
                        <>
                          <Button size="sm" onClick={() => act(a, "confirm")} data-testid={`confirm-adjustment-${a.id}`}>Confirm deduction</Button>
                          <Button size="sm" variant="ghost" onClick={() => act(a, "dismiss")} data-testid={`dismiss-adjustment-${a.id}`}>Dismiss</Button>
                        </>
                      )}
                      {(a.status === "deducted" || a.status === "refill_requested") && (
                        <Button size="sm" variant="outline" disabled={!a.customer_paid} title={!a.customer_paid ? "Mark the invoice paid first" : ""} onClick={() => act(a, "refill")} data-testid={`refill-adjustment-${a.id}`}>Confirm refill</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {doneAdj.length > 0 && (
          <p className="px-4 py-2 text-xs text-muted-foreground border-t border-border">{doneAdj.length} settled / dismissed adjustment(s) in history.</p>
        )}
      </Card>

      {!deposits ? (
        <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : deposits.length === 0 ? (
        <Empty>No deposits yet.</Empty>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {deposits.map((d) => {
            const pct = d.credit_limit ? Math.min(100, (d.outstanding_credit / d.credit_limit) * 100) : 0;
            return (
              <Card key={d.id} className="p-5" data-testid={`deposit-card-${d.agent_id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold">{d.agent_name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="secondary">{d.tier}</Badge>
                      <Badge variant={d.status === "held" ? "default" : d.status === "refunded" ? "outline" : "destructive"}>
                        {d.status}
                      </Badge>
                      {d.topup_required && <Badge variant="destructive">Top-up required</Badge>}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Deposit held</p>
                    <p className="font-display font-bold">{money(d.amount_held)}</p>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-muted-foreground">Credit used · unsold stock + unpaid invoices</span>
                    <span className={d.over_limit ? "text-destructive font-semibold" : ""}>
                      {money(d.outstanding_credit)} / {money(d.credit_limit)}
                    </span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${d.over_limit ? "bg-destructive" : d.near_limit ? "bg-amber-500" : "bg-primary"}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  {d.near_limit && (
                    <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Near / over credit limit
                    </p>
                  )}
                </div>

                {d.form && (
                  <p className="text-xs text-muted-foreground mt-3">
                    Form: {d.form} · Ref: {d.form_reference || d.reference || "—"}
                  </p>
                )}

                <div className="flex flex-wrap gap-2 mt-4">
                  <Button size="sm" onClick={() => setStockFor(d)} data-testid={`stock-${d.agent_id}`}><Package className="w-3.5 h-3.5 mr-1" /> Stock</Button>
                  {isAdmin && <Button size="sm" variant="outline" onClick={() => openModal("receive", d)} data-testid={`receive-deposit-${d.agent_id}`}>Record deposit</Button>}
                  {isAdmin && <Button size="sm" variant="outline" onClick={() => openModal("shortfall", d)} data-testid={`shortfall-${d.agent_id}`}>Record shortfall</Button>}
                  {isAdmin && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => openModal("refund", d)} data-testid={`refund-${d.agent_id}`}>Refund on exit</Button>}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {stockFor && <StockDialog dep={stockFor} onClose={() => { setStockFor(null); load(); }} />}

      <Dialog open={!!modal} onOpenChange={(v) => !v && setModal(null)}>
        <DialogContent>
          {modal && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {modal.type === "receive" && `Record deposit · ${modal.dep.agent_name}`}
                  {modal.type === "shortfall" && `Shortfall recovery · ${modal.dep.agent_name}`}
                  {modal.type === "refund" && `Refund deposit · ${modal.dep.agent_name}`}
                </DialogTitle>
              </DialogHeader>

              {modal.type === "receive" && (
                <div className="space-y-3">
                  <Fld label="Amount (AED)"><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="deposit-amount-input" /></Fld>
                  <Fld label="Form">
                    <Select value={form.form} onValueChange={(v) => setForm({ ...form, form: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                        <SelectItem value="post_dated_cheque">Post-dated cheque</SelectItem>
                        <SelectItem value="bank_guarantee">Bank guarantee</SelectItem>
                      </SelectContent>
                    </Select>
                  </Fld>
                  <Fld label="Reference"><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="deposit-ref-input" /></Fld>
                </div>
              )}
              {modal.type === "shortfall" && (
                <div className="space-y-3">
                  <Fld label="Month"><Input value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} /></Fld>
                  <Fld label="Shortfall amount (AED)"><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="shortfall-amount-input" /></Fld>
                  <Fld label="Reason"><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="shortfall-reason-input" /></Fld>
                  <p className="text-xs text-muted-foreground">Recovered first from pending commission, remainder from the deposit.</p>
                </div>
              )}
              {modal.type === "refund" && (
                <p className="text-sm text-muted-foreground">
                  Refund the full held deposit of {money(modal.dep.amount_held)} and mark the agent inactive.
                  Only do this once all stock is settled and there are no open disputes.
                </p>
              )}

              <DialogFooter>
                <Button onClick={submit} disabled={saving} data-testid="deposit-modal-submit">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Fld({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;
}
